import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Form,
  Select,
  Input,
  Button,
  Space,
  Card,
  Switch,
  DatePicker,
  InputNumber,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { RangePicker } = DatePicker;

const fieldOptions = [
  { value: "optedIn", label: "Opted In", type: "boolean" },
  { value: "createdAt", label: "Created At", type: "date" },
  { value: "emailId", label: "Email", type: "string" },
  { value: "userName", label: "Username", type: "string" },
  { value: "tag", label: "Tag", type: "string" },
  { value: "statusCode", label: "Status Code", type: "number" },
  { value: "phoneNumber", label: "Phone Number", type: "string" },
  { value: "lastLogin", label: "Last Login", type: "date" },
  { value: "subscriptionTier", label: "Subscription Tier", type: "string" },
  { value: "purchaseCount", label: "Purchase Count", type: "number" },
  { value: "totalSpent", label: "Total Spent", type: "number" },
  { value: "country", label: "Country", type: "string" },
  { value: "deviceType", label: "Device Type", type: "string" },
];

const operatorOptions = {
  boolean: [
    { value: "=", label: "is" },
    { value: "!=", label: "is not" },
  ],
  string: [
    { value: "=", label: "equals" },
    { value: "!=", label: "not equals" },
    { value: "contains", label: "contains" },
    { value: "startsWith", label: "starts with" },
    { value: "endsWith", label: "ends with" },
    { value: "in", label: "in list" },
    { value: "notIn", label: "not in list" },
    { value: "regex", label: "matches regex" },
  ],
  number: [
    { value: "=", label: "equals" },
    { value: "!=", label: "not equals" },
    { value: ">", label: "greater than" },
    { value: "<", label: "less than" },
    { value: ">=", label: "greater than or equal" },
    { value: "<=", label: "less than or equal" },
    { value: "between", label: "between" },
  ],
  date: [
    { value: "=", label: "on" },
    { value: "!=", label: "not on" },
    { value: ">", label: "after" },
    { value: "<", label: "before" },
    { value: "lastDays", label: "last X days" },
    { value: "nextDays", label: "next X days" },
    { value: "between", label: "between dates" },
    { value: "today", label: "today" },
    { value: "thisWeek", label: "this week" },
    { value: "thisMonth", label: "this month" },
    { value: "thisYear", label: "this year" },
  ],
};

const groupOperators = [
  { value: "AND", label: "All (AND)" },
  { value: "OR", label: "Any (OR)" },
  { value: "NOR", label: "None (NOR)" },
];

export default function RuleBuilder({ value = [], onChange }) {
  const [groups, setGroups] = useState([
    { id: 1, operator: "AND", rules: [], collapsed: false },
  ]);

  const getFieldType = (field) =>
    fieldOptions.find((f) => f.value === field)?.type;

  const normalizeDateValueForEdit = (value) => {
    if (!value) return undefined;
    const candidate = dayjs(value);
    return candidate.isValid() ? candidate : undefined;
  };

  const normalizeRuleForEdit = (rule, indexBase) => {
    const valueType = getFieldType(rule.field) || rule.valueType;
    let normalizedValue = rule.value;

    if (valueType === "date") {
      if (rule.operator === "between" && Array.isArray(rule.value)) {
        const start = normalizeDateValueForEdit(rule.value[0]);
        const end = normalizeDateValueForEdit(rule.value[1]);
        normalizedValue = start && end ? [start, end] : undefined;
      } else if (
        ["today", "thisWeek", "thisMonth", "thisYear"].includes(rule.operator)
      ) {
        normalizedValue = undefined;
      } else {
        normalizedValue = normalizeDateValueForEdit(rule.value);
      }
    }

    return {
      id: rule.id || Date.now() + indexBase,
      field: rule.field ?? "optedIn",
      operator: rule.operator ?? "=",
      value: normalizedValue,
      valueType,
    };
  };

  useEffect(() => {
    if (!Array.isArray(value)) return;

    if (value.length === 0) {
      setGroups([{ id: 1, operator: "AND", rules: [], collapsed: false }]);
      return;
    }

    const isGroupArray = value.some((item) => Array.isArray(item?.rules));
    if (isGroupArray) {
      const hydratedGroups = value.map((group, groupIndex) => ({
        id: group.id || Date.now() + groupIndex,
        operator: group.operator || "AND",
        collapsed: false,
        rules: (group.rules || []).map((rule, ruleIndex) => ({
          ...normalizeRuleForEdit(rule, groupIndex * 1000 + ruleIndex),
        })),
      }));
      setGroups(hydratedGroups);
      return;
    }

    const hydratedRules = value.map((rule, index) =>
      normalizeRuleForEdit(rule, index)
    );

    setGroups([
      {
        id: 1,
        operator: "AND",
        rules: hydratedRules,
        collapsed: false,
      },
    ]);
  }, [value]);

  const addGroup = () => {
    const newGroup = {
      id: Date.now(),
      operator: "AND",
      rules: [],
      collapsed: false,
    };
    const updated = [...groups, newGroup];
    setGroups(updated);
    notifyChange(updated);
  };

  const removeGroup = (groupId) => {
    const updated = groups.filter((g) => g.id !== groupId);
    setGroups(updated);
    notifyChange(updated);
  };

  const addRule = (groupId) => {
    const updated = groups.map((group) => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: [
            ...group.rules,
            {
              id: Date.now(),
              field: "optedIn",
              operator: "=",
              value: true,
            },
          ],
        };
      }
      return group;
    });
    setGroups(updated);
    notifyChange(updated);
  };

  const removeRule = (groupId, ruleId) => {
    const updated = groups.map((group) => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: group.rules.filter((r) => r.id !== ruleId),
        };
      }
      return group;
    });
    setGroups(updated);
    notifyChange(updated);
  };

  const updateRule = (groupId, ruleId, updates) => {
    const updated = groups.map((group) => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: group.rules.map((rule) =>
            rule.id === ruleId ? { ...rule, ...updates } : rule
          ),
        };
      }
      return group;
    });
    setGroups(updated);
    notifyChange(updated);
  };

  const updateGroupOperator = (groupId, operator) => {
    const updated = groups.map((group) =>
      group.id === groupId ? { ...group, operator } : group
    );
    setGroups(updated);
    notifyChange(updated);
  };

  const toggleGroup = (groupId) => {
    const updated = groups.map((group) =>
      group.id === groupId ? { ...group, collapsed: !group.collapsed } : group
    );
    setGroups(updated);
  };

  const notifyChange = (updatedGroups) => {
    const normalizedGroups = updatedGroups.map((group) => ({
      id: group.id,
      operator: group.operator,
      rules: (group.rules || []).map((rule) => ({
        id: rule.id,
        field: rule.field,
        operator: rule.operator,
        valueType: getFieldType(rule.field) || rule.valueType,
        value:
          getFieldType(rule.field) === "date"
            ? rule.operator === "between"
              ? Array.isArray(rule.value)
                ? rule.value.map((date) =>
                    dayjs(date).isValid()
                      ? dayjs(date).toISOString()
                      : date
                  )
                : []
              : rule.value && dayjs(rule.value).isValid()
              ? dayjs(rule.value).toISOString()
              : rule.value
            : rule.value,
      })),
    }));
    onChange && onChange(normalizedGroups);
  };

  const renderValueInput = (rule, groupId, ruleId) => {
    const field = fieldOptions.find((f) => f.value === rule.field);
    if (!field) return null;

    switch (field.type) {
      case "boolean":
        return (
          <Switch
            checked={rule.value}
            onChange={(checked) =>
              updateRule(groupId, ruleId, { value: checked })
            }
          />
        );

      case "number":
        if (rule.operator === "between") {
          return (
            <Space>
              <InputNumber
                placeholder="Min"
                value={rule.value?.min}
                onChange={(min) =>
                  updateRule(groupId, ruleId, {
                    value: { ...rule.value, min },
                  })
                }
              />
              <span>to</span>
              <InputNumber
                placeholder="Max"
                value={rule.value?.max}
                onChange={(max) =>
                  updateRule(groupId, ruleId, {
                    value: { ...rule.value, max },
                  })
                }
              />
            </Space>
          );
        }
        return (
          <InputNumber
            value={rule.value}
            onChange={(value) => updateRule(groupId, ruleId, { value })}
          />
        );

      case "date":
        if (["lastDays", "nextDays"].includes(rule.operator)) {
          return (
            <InputNumber
              addonAfter="days"
              value={rule.value}
              onChange={(value) => updateRule(groupId, ruleId, { value })}
            />
          );
        }
        if (rule.operator === "between") {
          return (
            <RangePicker
              value={rule.value}
              onChange={(dates) => updateRule(groupId, ruleId, { value: dates })}
            />
          );
        }
        return (
          <DatePicker
            value={rule.value}
            onChange={(date) => updateRule(groupId, ruleId, { value: date })}
          />
        );

      case "string":
        if (["in", "notIn"].includes(rule.operator)) {
          return (
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Enter values (press Enter to add)"
              value={rule.value || []}
              onChange={(values) =>
                updateRule(groupId, ruleId, { value: values })
              }
            />
          );
        }
        return (
          <Input
            value={rule.value}
            onChange={(e) =>
              updateRule(groupId, ruleId, { value: e.target.value })
            }
            placeholder="Enter value"
          />
        );

      default:
        return (
          <Input
            value={rule.value}
            onChange={(e) =>
              updateRule(groupId, ruleId, { value: e.target.value })
            }
          />
        );
    }
  };

  return (
    <div className="rule-builder">
      {groups.map((group) => (
        <Card
          key={group.id}
          size="small"
          title={
            <Space>
              <Select
                value={group.operator}
                onChange={(value) => updateGroupOperator(group.id, value)}
                style={{ width: 120 }}
              >
                {groupOperators.map((op) => (
                  <Option key={op.value} value={op.value}>
                    {op.label}
                  </Option>
                ))}
              </Select>
              <span>of the following rules</span>
              <Tag color="blue">{group.rules.length} rules</Tag>
            </Space>
          }
          extra={
            <Space>
              <Button
                type="text"
                icon={group.collapsed ? <DownOutlined /> : <UpOutlined />}
                onClick={() => toggleGroup(group.id)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeGroup(group.id)}
                disabled={groups.length === 1}
              />
            </Space>
          }
          className="rule-group-card"
        >
          {!group.collapsed && (
            <div className="rules-list">
              {group.rules.map((rule) => (
                <div key={rule.id} className="rule-row">
                  <Space>
                    <Select
                      value={rule.field}
                      onChange={(value) =>
                        updateRule(group.id, rule.id, {
                          field: value,
                          operator: "=",
                          value: undefined,
                        })
                      }
                      style={{ width: 180 }}
                    >
                      {fieldOptions.map((field) => (
                        <Option key={field.value} value={field.value}>
                          {field.label}
                        </Option>
                      ))}
                    </Select>

                    <Select
                      value={rule.operator}
                      onChange={(value) =>
                        updateRule(group.id, rule.id, {
                          operator: value,
                          value: undefined,
                        })
                      }
                      style={{ width: 150 }}
                    >
                      {operatorOptions[
                        fieldOptions.find((f) => f.value === rule.field)?.type
                      ]?.map((op) => (
                        <Option key={op.value} value={op.value}>
                          {op.label}
                        </Option>
                      ))}
                    </Select>

                    {renderValueInput(rule, group.id, rule.id)}

                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeRule(group.id, rule.id)}
                      disabled={group.rules.length === 1}
                    />
                  </Space>
                </div>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => addRule(group.id)}
                block
              >
                Add Rule
              </Button>
            </div>
          )}
        </Card>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addGroup}
        style={{ marginTop: 16 }}
      >
        Add Condition Group
      </Button>
    </div>
  );
}